import { Temporal } from 'temporal-polyfill'
import { calendarDay, type CalendarDay } from '../validators/temporal.validator.js'

/**
 * The two-week periods ad numbers are grouped into (docs/ad-analytics.spec.md,
 * "Two-week periods").
 *
 * Fixed fortnights, each running from a Sunday through the second Saturday
 * after, counted forward and back from one Sunday. They are the same for every
 * company and venue: a period's dates never move, and two venues' periods always
 * line up.
 *
 * Every day here is an ad account's own day, as Google reports it. Nothing in
 * this file knows a time zone, and nothing needs to.
 */

/** The Sunday the periods are counted from. Any Sunday would do; this one is fixed for good. */
export const AD_PERIOD_ANCHOR: CalendarDay = calendarDay('2026-01-04')

/** How many days a period holds. */
export const AD_PERIOD_DAYS = 14

/** How many months of history the first import reaches back, before the current one. */
const AD_HISTORY_MONTHS = 24

export interface AdPeriod {
  /** Its first day, a Sunday. */
  start: CalendarDay
  /** Its last day, the second Saturday after. */
  end: CalendarDay
}

/** What a period counts, and what it is compared with. */
export interface AdPeriodWindow {
  period: AdPeriod
  /** The last day counted: the period's end, or the cut-off while it is in progress. */
  through: CalendarDay
  /** How many days are counted, from 1 to 14. */
  days: number
  inProgress: boolean
  /** The first day of the period before. */
  compareStart: CalendarDay
  /**
   * The last day of the period before that counts in the comparison: all of it
   * for a finished period, or as many days as this one holds for one in
   * progress, so day 3 is compared with day 3.
   */
  compareEnd: CalendarDay
}

function plain(day: CalendarDay): Temporal.PlainDate {
  return Temporal.PlainDate.from(day)
}

function dayOf(date: Temporal.PlainDate): CalendarDay {
  return calendarDay(date.toString())
}

/** `day` moved by a number of days, back when negative. */
export function addDays(day: CalendarDay, days: number): CalendarDay {
  return dayOf(plain(day).add({ days }))
}

/** Whole days from `from` to `to`; negative when `to` comes first. */
export function daysBetween(from: CalendarDay, to: CalendarDay): number {
  return plain(from).until(plain(to), { largestUnit: 'days' }).days
}

/**
 * The day `at` falls on in an account's own zone — the zone its days are
 * reported in. A zone the platform named that this runtime doesn't know is read
 * as UTC rather than failing an import over it.
 */
export function dayInZone(at: Date, timeZone: string): CalendarDay {
  const instant = Temporal.Instant.fromEpochMilliseconds(at.getTime())
  try {
    return dayOf(instant.toZonedDateTimeISO(timeZone).toPlainDate())
  } catch {
    return dayOf(instant.toZonedDateTimeISO('UTC').toPlainDate())
  }
}

/** The hour of the day (0–23) `at` falls in, in an account's own zone. Unknown zones read as UTC. */
export function hourInZone(at: Date, timeZone: string): number {
  const instant = Temporal.Instant.fromEpochMilliseconds(at.getTime())
  try {
    return instant.toZonedDateTimeISO(timeZone).hour
  } catch {
    return instant.toZonedDateTimeISO('UTC').hour
  }
}

/** The period a day falls in. */
export function adPeriodOf(day: CalendarDay): AdPeriod {
  // Floor, not truncate: a day before the anchor belongs to a period that
  // starts before it too, never to the anchor's own.
  const index = Math.floor(daysBetween(AD_PERIOD_ANCHOR, day) / AD_PERIOD_DAYS)
  const start = addDays(AD_PERIOD_ANCHOR, index * AD_PERIOD_DAYS)
  return { start, end: addDays(start, AD_PERIOD_DAYS - 1) }
}

/** True when `day` is the first day of a period. */
export function isAdPeriodStart(day: CalendarDay): boolean {
  return adPeriodOf(day).start === day
}

/** The period just before this one. */
export function previousAdPeriod(period: AdPeriod): AdPeriod {
  return adPeriodOf(addDays(period.start, -1))
}

/** Every period holding a day between `from` and `to`, newest first. Empty when `to` comes first. */
export function adPeriodsBetween(from: CalendarDay, to: CalendarDay): AdPeriod[] {
  if (daysBetween(from, to) < 0) return []
  const periods: AdPeriod[] = []
  const first = adPeriodOf(from).start
  for (let period = adPeriodOf(to); daysBetween(first, period.start) >= 0; period = previousAdPeriod(period)) {
    periods.push(period)
  }
  return periods
}

/**
 * Where an account's first import begins: the start of the period that holds
 * the first day of the month 24 months before `today`'s. An account put in use
 * on 2026-10-14 is imported from 2024-09-29, so every period shown is whole.
 */
export function adHistoryStart(today: CalendarDay): CalendarDay {
  const firstOfMonth = plain(today).with({ day: 1 }).subtract({ months: AD_HISTORY_MONTHS })
  return adPeriodOf(dayOf(firstOfMonth)).start
}

/**
 * What `period` counts when the newest day there are numbers for is `cutoff`,
 * and what it is compared with.
 *
 * A period the cut-off has passed is whole, and is compared with the whole period
 * before. One the cut-off is inside is in progress: it counts up to the cut-off,
 * and is compared with the same number of days at the start of the period
 * before, so a period three days old isn't made to look like a collapse.
 *
 * @throws RangeError when the cut-off comes before the period starts: such a
 *   period holds no day, and the newest period to show is the cut-off's own.
 */
export function adPeriodWindow(period: AdPeriod, cutoff: CalendarDay): AdPeriodWindow {
  if (daysBetween(period.start, cutoff) < 0) {
    throw new RangeError(`Period ${period.start} holds no day up to ${cutoff}`)
  }
  const inProgress = daysBetween(cutoff, period.end) > 0
  const through = inProgress ? cutoff : period.end
  const days = daysBetween(period.start, through) + 1
  const before = previousAdPeriod(period)
  return {
    period,
    through,
    days,
    inProgress,
    compareStart: before.start,
    compareEnd: addDays(before.start, days - 1),
  }
}
